import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SuperadminSettings: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('superadminUser');
    const token = localStorage.getItem('superadminToken');

    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      setFormData({
        first_name: parsed.first_name || '',
        last_name: parsed.last_name || '',
        email: parsed.email || '',
        password: ''
      });
    } else if (token) {
      try {
        // Fallback: decode JWT if user object wasn't stored (e.g. they logged in before the update)
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ id: payload.admin_id, email: payload.email });
        setFormData(prev => ({ ...prev, email: payload.email }));
      } catch (e) {
        console.error("Could not parse token");
      }
    }
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    try {
      const payload = {
        admin_id: user.id,
        ...formData
      };
      const res = await axios.put('http://localhost:8000/api/superadmin/profile', payload);
      
      // Update local storage
      localStorage.setItem('superadminUser', JSON.stringify(res.data.data.user));
      alert('Profile updated successfully!');
      setFormData({ ...formData, password: '' });
      
      // We force a quick reload so layout updates name/initials
      window.location.reload();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Profile Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your superadmin credentials and personal details.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-[10px] shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">Account Information</h2>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">First Name</label>
              <input 
                type="text" 
                value={formData.first_name} 
                onChange={e => setFormData({...formData, first_name: e.target.value})} 
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" 
                required 
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Last Name</label>
              <input 
                type="text" 
                value={formData.last_name} 
                onChange={e => setFormData({...formData, last_name: e.target.value})} 
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" 
                required 
              />
            </div>
          </div>
          
          <div>
            <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">Master Email Address</label>
            <input 
              type="email" 
              value={formData.email} 
              onChange={e => setFormData({...formData, email: e.target.value})} 
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" 
              required 
            />
            <p className="text-[11px] text-slate-500 mt-1">This email is used to log into the Master Console.</p>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Change Security Password</h3>
            <div>
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">New Password (Leave blank to keep current)</label>
              <input 
                type="password" 
                value={formData.password} 
                onChange={e => setFormData({...formData, password: e.target.value})} 
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" 
                placeholder="••••••••" 
              />
            </div>
          </div>

          <div className="pt-4 flex justify-end">
            <button 
              type="submit" 
              disabled={isSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm py-2 px-6 rounded-md transition-all shadow-sm shadow-indigo-200"
            >
              {isSaving ? 'Saving Changes...' : 'Save Profile Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuperadminSettings;
